import toast from 'react-hot-toast';

/**
 * Show a success toast with translation support
 * @param t - Translation function
 * @param messageKey - Translation key (e.g., "teachers.messages.createSuccess")
 * @param i18n - i18n instance for RTL detection
 * @param fallback - Fallback message (can be dynamic API response or static fallback)
 */
export const showToastSuccess = (
  t: (key: string) => string,
  messageKey: string,
  i18n: any,
  fallback?: string
) => {
  // Try to translate the key
  const translated = t(messageKey);
  
  // Determine the message to show:
  // 1. If translation exists and is different from key, use it
  // 2. Otherwise, use fallback if provided
  // 3. Finally, default to 'Success'
  const message = (translated && translated !== messageKey) 
    ? translated 
    : (fallback || 'Success');
  
  const isRTL = i18n.language === 'fa' || i18n.language === 'ps';
  
  toast.success(message, {
    duration: 4000,
    style: {
      direction: isRTL ? 'rtl' : 'ltr',
    },
  });
};

/**
 * Show an error toast with translation support
 * @param t - Translation function
 * @param messageKey - Translation key (e.g., "teachers.messages.saveError")
 * @param i18n - i18n instance for RTL detection
 * @param fallback - Fallback message (can be dynamic API response or static fallback)
 */
export const showToastError = (
  t: (key: string) => string,
  messageKey: string,
  i18n: any,
  fallback?: string
) => {
  // Try to translate the key
  const translated = t(messageKey);
  
  // Determine the message to show:
  // 1. If translation exists and is different from key, use it
  // 2. Otherwise, use fallback if provided (this is where API error messages appear)
  // 3. Finally, default to 'Error'
  const message = (translated && translated !== messageKey) 
    ? translated 
    : (fallback || 'Error');
  
  const isRTL = i18n.language === 'fa' || i18n.language === 'ps';
  
  toast.error(message, {
    duration: 4000,
    style: {
      direction: isRTL ? 'rtl' : 'ltr',
    },
  });
};

export const showToast = (
  t: (key: string) => string,
  messageKey: string,
  i18n: any,
  type: 'success' | 'error' = 'success',
  fallback?: string
) => {
  if (type === 'success') {
    showToastSuccess(t, messageKey, i18n, fallback);
  } else {
    showToastError(t, messageKey, i18n, fallback);
  }
};
